import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import {
  useUserID as useCurrentUserID,
  useShouldInvalidateActiveHouseholdDetail,
} from "Flocknote/stateHooks";

import {
  useCategorizedMembers,
  useCategorizedNetworks,
} from "HouseholdProfile/transformHooks";

import { useUserID } from "Utilities/urlParamHooks";

import householdProfileKeys from "./keys";
import { useGetHouseholdDataByUserID, useGetHouseholdDetail } from "./queries";

/**
 * @returns {HouseholdProfile}
 */
const useHouseholdData = () => {
  const { data: { householdProfile } = {}, isLoading } =
    useGetHouseholdDetail();

  return { ...householdProfile, isLoading };
};

/**
 * used to update extended fields data
 */
const useHohID = () => {
  const [{ userID } = {}] = useHohs();
  const [{ userID: backupUserID } = {}] = useNonHohs();

  return userID || backupUserID;
};

/**
 * @returns {[HouseholdMember]}
 */
const useHohs = () => {
  const { members } = useHouseholdData();
  const { hohs } = useCategorizedMembers(members);

  return hohs;
};

/**
 * Anyone who has the isChild flag marked "false" with hoh coming first
 * @returns {[HouseholdMember]}
 */
const useAdults = () => {
  const { members } = useHouseholdData();
  const { hohs, dependents } = useCategorizedMembers(members);

  return [...hohs, ...dependents];
};

/**
 * Anyone who has the isChild flag marked "true"
 * @returns {[HouseholdMember]}
 */
const useChildren = () => {
  const { members } = useHouseholdData();
  const { children } = useCategorizedMembers(members);

  return children.sort(
    ({ birthday: a }, { birthday: b }) =>
      Number(new Date(a)) - Number(new Date(b))
  );
};

/**
 * @returns {[HouseholdMember]}
 */
const useNonHohs = () => {
  const { members } = useHouseholdData();
  const { nonHohs } = useCategorizedMembers(members);

  return nonHohs;
};

/**
 * @returns {HouseholdNetwork}
 */
const usePrimaryNetwork = () => {
  const { networks } = useHouseholdData();
  const { primary } = useCategorizedNetworks(networks);

  return primary || {};
};

/**
 * @returns {[HouseholdNetwork]}
 */
const useSecondaryNetworks = () => {
  const { networks } = useHouseholdData();
  const { secondary } = useCategorizedNetworks(networks);

  return secondary;
};

const useClearGetHouseholdDataByUserID = () => {
  const userID = useUserID();
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (userID) {
      // clear queries householdData for user and their household.
      queryClient.invalidateQueries({
        queryKey: householdProfileKeys.householdDetail(userID),
      });
      queryClient.invalidateQueries({
        queryKey: householdProfileKeys.getHouseholdDataByUserID(userID),
      });
    }
  }, [queryClient, userID]);
};

const useIsSingleUserHousehold = () => {
  const { members } = useHouseholdData();

  const isSingleUserHousehold = (members?.length || 0) < 2;

  return isSingleUserHousehold;
};

const useIsHouseholdGroupingEnabled = () => {
  const userID = useUserID();
  const { data: { householdGroupingEnabled } = {} } =
    useGetHouseholdDataByUserID(userID);

  return householdGroupingEnabled;
};

const useInvalidateActiveHouseholdDetailViaLegacy = () => {
  const shouldInvalidateActiveHouseholdDetail =
    useShouldInvalidateActiveHouseholdDetail();

  const userID = useUserID();
  const queryClient = useQueryClient();
  const queryKey = householdProfileKeys.householdDetail(userID);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(queryKey);
  }, [queryClient, queryKey]);

  useEffect(() => {
    if (shouldInvalidateActiveHouseholdDetail) {
      invalidate();
      window.fnReactBus.setShouldInvalidateActiveHouseholdDetail(false);
    }
  });
};

const useCurrentUserIsInHousehold = () => {
  const { members = [] } = useHouseholdData();
  const currentUser = useCurrentUserID();

  return members.find(({ userID }) => Number(userID) === Number(currentUser));
};

export {
  useHouseholdData,
  useHohs,
  useAdults,
  useChildren,
  useNonHohs,
  usePrimaryNetwork,
  useSecondaryNetworks,
  useHohID,
  useClearGetHouseholdDataByUserID,
  useIsSingleUserHousehold,
  useIsHouseholdGroupingEnabled,
  useInvalidateActiveHouseholdDetailViaLegacy,
  useCurrentUserIsInHousehold,
};
